import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { resolve } from 'inversify-react';

import { DataLocalService } from 'src/core/services/data-local.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { EntitySchemaFormView } from './entity-schema-form.view';
import { EntitySchemaDataService } from '../entity-schema.data';
import { EntitySchemaPropertyItem, EntitySchemaPropertyBuilder } from './entity-schema-form.data';

interface Props extends ControllerProps<{ type: string }> {
}

interface State {
	mode: ControlMode;
	dataSet: DataSet<EntitySchemaPropertyItem>;
}

@observer
export class EntitySchemaFormController extends React.Component<Props, State> {
	@observable
	dataService: EntitySchemaDataService;

	@resolve
	toast: ToastNotifications;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'edit',
			dataSet: new DataLocalService<EntitySchemaPropertyItem>([])
		};
	}

	componentDidMount(): void {
		this.props.setBusy(true);

		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then((items) => {
				this.props.setBusy(false);
			});
	}

	private async loadData(): Promise<void> {
		this.dataService = EntitySchemaDataService.make();

		this.dataService.addFilter('name', 'eq', this.props.match.params.type);
		this.dataService.addSorting('name', 'asc', true);
		this.dataService.pageSize = 1;
		const [item] = await this.dataService.load(query => query);

		const jsonSchema = ObjectUtils.toObject<JsonSchema>(item.schema);
		this.setState({
			dataSet: new DataLocalService(EntitySchemaPropertyBuilder.getAdditionalProperties(jsonSchema))
		});
	}

	private validateDataSet(): boolean {
		const { items } = this.state.dataSet;
		for (let i = 0; i < items.length; i++) {
			const errors = this.isValidPropertyItem(items[i]);
			if (errors.length > 0) {
				this.toast.showToast('Invalid item', errors, 'error');
				return false;
			}
		}

		return true;
	}

	handleModeChange = (mode: ControlMode): void => {
		if (!this.validateDataSet()) {
			return;
		}

		this.setState({
			mode: mode
		});
	};

	createPropertyItem = (): Promise<EntitySchemaPropertyItem> => {
		const newItem = new EntitySchemaPropertyItem();

		const items = [...this.state.dataSet.items, newItem];
		const dataSet = new DataLocalService<EntitySchemaPropertyItem>(items);
		dataSet.isDirty = true;

		this.setState({ dataSet });
		return Promise.resolve(newItem);
	};

	handleEditPropertyItem = (item: EntitySchemaPropertyItem): void => {
		const dataSet = new DataLocalService<EntitySchemaPropertyItem>(this.state.dataSet.items);
		dataSet.isDirty = true;

		this.setState({ dataSet });
	};

	handleAfterDeletePropertyItem = (): void => {
		const dataSet = new DataLocalService<EntitySchemaPropertyItem>(this.state.dataSet.items);
		dataSet.isDirty = true;

		this.setState({ dataSet });
	};

	isValidPropertyItem = (item: EntitySchemaPropertyItem): React.ReactNode[] => {
		const items = this.state.dataSet.items;
		const errors = item.validate();
		if (items.filter(i => i.name === item.name).length > 1) {
			errors.push(<p>The names must be unique. The name <b>{item.name}</b> is duplicated.</p>);
		}
		if (items.filter(i => i.label === item.label).length > 1) {
			errors.push(<p>The labels must be unique. The label <b>${item.label}</b> is duplicated</p>);
		}

		return errors;
	};

	swapDown = (index: number): void => {
		const items = this.state.dataSet.items;
		const tmp = items[index];
		items[index] = items[index + 1];
		items[index + 1] = tmp;

		const dataSet = new DataLocalService(items);
		dataSet.isDirty = true;

		this.setState({ dataSet });
	};

	handleSave = (): void => {
		if (!this.validateDataSet()) {
			return;
		}

		const items = this.state.dataSet.items;
		const extension: JsonSchema = {
			type: 'object',
			properties: items
				.map((item) => item.toJsonSchemaProperty())
				.reduce((accumulator, currentItem) => Object.assign(accumulator, currentItem), {}),
			required: items.filter((item) => item.required)
				.map((item) => item.name)
		};

		const entitySchema = this.dataService.item;
		const schema = ObjectUtils.toObject<JsonSchema>(entitySchema.schema);
		schema.allOf = [extension];
		entitySchema.schema = ObjectUtils.toString(schema);

		this.props.setBusy(true);
		this.dataService.save()
			.then(() => {
				this.props.setBusy(false);
				this.handleModeChange('display');
				this.toast.showDataSaved();
				this.props.history.replace(`/general/entity-schemas`);
			})
			.catch((error) => {
				this.props.setBusy(false);
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			});
	};

	handleCancel = (): void => {
		this.props.history.push('/general/entity-schemas');
	};

	render(): React.ReactNode {
		const { dataSet } = this.state;

		return (
			<EntitySchemaFormView
				model={dataSet}
				mode={this.state.mode}
				onModeChange={this.handleModeChange}
				onSave={this.handleSave}
				onCancel={this.handleCancel}
				isValidPropertyItem={this.isValidPropertyItem}
				createPropertyItem={this.createPropertyItem}
				onEditPropertyItem={this.handleEditPropertyItem}
				afterDeletePropertyItem={this.handleAfterDeletePropertyItem}
				swapDown={this.swapDown}
				setBusy={this.props.setBusy}
				schemaName={this.props.match.params.type}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/entity-schemas/form/entity-schema-form.controller.tsx